import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Ticket, X, Plus, Gift, CreditCard, ChevronDown, ChevronUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import SectionBand from '@/components/ui/SectionBand';
import { REVIEW_SECTION_CARD } from '@/constants/brandColors';
import { fmtAmount } from '@/utils/money';

const AIRLINES = [
  { code: 'AA', name: 'American Airlines' },
  { code: 'DL', name: 'Delta Air Lines' },
  { code: 'UA', name: 'United Airlines' },
  { code: 'WN', name: 'Southwest Airlines' },
  { code: 'B6', name: 'JetBlue Airways' },
  { code: 'AS', name: 'Alaska Airlines' },
  { code: 'NK', name: 'Spirit Airlines' },
  { code: 'F9', name: 'Frontier Airlines' },
  { code: 'G4', name: 'Allegiant Air' },
  { code: 'HA', name: 'Hawaiian Airlines' },
  { code: 'SY', name: 'Sun Country Airlines' },
  { code: 'AC', name: 'Air Canada' },
  { code: 'AM', name: 'Aeromexico' },
  { code: 'BA', name: 'British Airways' },
  { code: 'LH', name: 'Lufthansa' },
  { code: 'AF', name: 'Air France' },
  { code: 'KL', name: 'KLM' },
  { code: 'EK', name: 'Emirates' },
  { code: 'QR', name: 'Qatar Airways' },
  { code: 'SQ', name: 'Singapore Airlines' },
  { code: 'CX', name: 'Cathay Pacific' },
  { code: 'JL', name: 'Japan Airlines' },
  { code: 'NH', name: 'ANA' },
  { code: 'QF', name: 'Qantas' },
  { code: 'VA', name: 'Virgin Atlantic' },
  { code: 'VS', name: 'Virgin Atlantic' },
  { code: 'IB', name: 'Iberia' },
  { code: 'AY', name: 'Finnair' },
  { code: 'SK', name: 'SAS Scandinavian' },
  { code: 'LX', name: 'SWISS' },
  { code: 'OS', name: 'Austrian Airlines' },
  { code: 'TK', name: 'Turkish Airlines' },
  { code: 'EY', name: 'Etihad Airways' },
  { code: 'OTHER', name: 'Other Airline' }
];

const CREDIT_TYPES = [
  { value: 'travel_credit', label: 'Travel Credit / eCredit', icon: Ticket, description: 'Airline-issued credit from cancelled flight or voucher' },
  { value: 'certificate', label: 'Certificate / Voucher', icon: CreditCard, description: 'Paper or electronic certificate code' },
  { value: 'gift_card', label: 'Airline Gift Card', icon: Gift, description: 'Prepaid gift card from the airline' }
];

export default function TravelCreditsSection({ 
  appliedCredits = [], 
  onCreditsChange, 
  maxAmount,
  isAuthenticated = false,
  flightAirlineCode = null // Auto-select airline based on flight in cart
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showForm, setShowForm] = useState(false);
  
  // Find the airline name for the flight's airline code
  const flightAirline = flightAirlineCode ? AIRLINES.find(a => a.code === flightAirlineCode) : null;
  
  const [formData, setFormData] = useState({
    airlineCode: flightAirlineCode || '',
    creditType: '',
    reference: '',
    pin: '',
    claimantLastName: '',
    claimedAmount: ''
  });
  const [errors, setErrors] = useState({});
  
  // Update airline code when flight changes
  useEffect(() => {
    if (flightAirlineCode && formData.airlineCode !== flightAirlineCode) {
      setFormData(prev => ({ ...prev, airlineCode: flightAirlineCode }));
    }
  }, [flightAirlineCode]);

  const totalApplied = appliedCredits.reduce((sum, credit) => sum + credit.claimedAmount, 0);
  const remainingBalance = maxAmount - totalApplied;

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.airlineCode) {
      newErrors.airlineCode = 'Please select an airline';
    }
    
    if (!formData.creditType) {
      newErrors.creditType = 'Please select credit type';
    }
    
    if (!formData.reference.trim()) {
      newErrors.reference = 'Credit/card number is required';
    } else if (formData.reference.trim().length < 6) {
      newErrors.reference = 'Number must be at least 6 characters';
    }
    
    if (formData.creditType === 'travel_credit' && !formData.claimantLastName.trim()) {
      newErrors.claimantLastName = 'Last name is required for travel credits';
    }
    
    const amount = parseFloat(formData.claimedAmount);
    if (!formData.claimedAmount || isNaN(amount)) {
      newErrors.claimedAmount = 'Please enter a valid amount';
    } else if (amount < 5) {
      newErrors.claimedAmount = 'Minimum amount is $5';
    } else if (amount > remainingBalance) {
      newErrors.claimedAmount = `Maximum you can apply is $${fmtAmount(remainingBalance)}`;
    }
    
    const isDuplicate = appliedCredits.some(
      credit => credit.reference.toLowerCase() === formData.reference.trim().toLowerCase() &&
                credit.airlineCode === formData.airlineCode
    );
    if (isDuplicate) {
      newErrors.reference = 'This credit has already been applied';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddCredit = () => {
    if (!validateForm()) return;
    
    const newCredit = {
      id: `credit_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`,
      airlineCode: formData.airlineCode,
      airlineName: AIRLINES.find(a => a.code === formData.airlineCode)?.name || formData.airlineCode,
      creditType: formData.creditType,
      creditTypeLabel: CREDIT_TYPES.find(t => t.value === formData.creditType)?.label || formData.creditType,
      reference: formData.reference.trim(),
      pin: formData.pin.trim() || null,
      claimantLastName: formData.claimantLastName.trim() || null,
      claimedAmount: parseFloat(formData.claimedAmount),
      status: 'pending_verification',
      createdAt: new Date().toISOString()
    };
    
    const updatedCredits = [...appliedCredits, newCredit];
    onCreditsChange(updatedCredits);
    
    setFormData({
      airlineCode: flightAirlineCode || '',
      creditType: '',
      reference: '',
      pin: '',
      claimantLastName: '',
      claimedAmount: ''
    });
    setShowForm(false);
    setErrors({});
  };

  const handleRemoveCredit = (creditId) => {
    const updatedCredits = appliedCredits.filter(c => c.id !== creditId);
    onCreditsChange(updatedCredits);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const getTypeIcon = (type) => {
    const typeConfig = CREDIT_TYPES.find(t => t.value === type);
    if (!typeConfig) return Ticket;
    return typeConfig.icon;
  };

  const maskReference = (ref) => {
    if (ref.length <= 4) return ref;
    return '••••' + ref.slice(-4);
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className={REVIEW_SECTION_CARD}>
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full text-left hover:bg-brand-900/20 transition-colors"
        aria-expanded={isExpanded}
      >
        <SectionBand
          icon={Ticket}
          title="Travel Credits & Gift Cards"
          subtitle={
            totalApplied > 0
              ? `${appliedCredits.length} credit${appliedCredits.length !== 1 ? 's' : ''} applied`
              : 'Apply airline credits or gift cards'
          }
          rightSlot={
            <div className="flex items-center gap-2 flex-shrink-0">
              {totalApplied > 0 && (
                <span className="text-[11px] font-normal text-emerald-300 bg-emerald-400/10 border border-emerald-400/15 px-2.5 py-0.5 rounded-full whitespace-nowrap">
                  -${fmtAmount(totalApplied)}
                </span>
              )}
              {isExpanded ? (
                <ChevronUp className="w-4 h-4 text-white/45" />
              ) : (
                <ChevronDown className="w-4 h-4 text-white/45" />
              )}
            </div>
          }
        />
      </button>

      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 md:px-6 py-5">
              {appliedCredits.length > 0 && (
                <div className="space-y-3 mb-6">
                  {appliedCredits.map((credit) => {
                    const TypeIcon = getTypeIcon(credit.creditType);
                    return (
                      <div 
                        key={credit.id}
                        className="flex items-center justify-between bg-white/5 border border-white/10 rounded-2xl px-4 py-3"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-[var(--accent-color)]/20 flex items-center justify-center">
                            <TypeIcon className="w-5 h-5 text-[var(--accent-color)]" />
                          </div>
                          <div>
                            <div className="text-white font-normal text-sm">
                              {credit.airlineName} {credit.creditTypeLabel}
                            </div>
                            <div className="text-white/50 text-xs font-normal">
                              {maskReference(credit.reference)}
                              {credit.claimantLastName && ` • ${credit.claimantLastName}`}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <div className="text-green-400 font-medium">-${fmtAmount(credit.claimedAmount)}</div>
                            <div className="text-white/40 text-xs">Pending verification</div>
                          </div>
                          <button
                            onClick={() => handleRemoveCredit(credit.id)}
                            className="w-8 h-8 rounded-full bg-red-500/10 hover:bg-red-500/20 flex items-center justify-center transition-colors"
                          >
                            <X className="w-4 h-4 text-red-400" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {!showForm ? (
                <Button
                  onClick={() => setShowForm(true)}
                  variant="outline"
                  className="w-full border-dashed border-white/20 bg-transparent hover:bg-white/5 text-white/60 hover:text-white font-normal"
                  disabled={remainingBalance <= 0}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {appliedCredits.length > 0 ? 'Add Another Credit' : 'Add Travel Credit or Gift Card'}
                </Button>
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white/5 border border-white/10 rounded-2xl p-4 md:p-5"
                >
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="text-white font-normal">Add Credit or Gift Card</h4>
                    <button
                      onClick={() => {
                        setShowForm(false);
                        setErrors({});
                        setFormData({
                          airlineCode: flightAirlineCode || '',
                          creditType: '',
                          reference: '',
                          pin: '',
                          claimantLastName: '',
                          claimedAmount: ''
                        });
                      }}
                      className="text-white/40 hover:text-white"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-white/60 text-sm mb-2 block">Airline</Label>
                        {/* Always locked to flight's airline - credits can only be applied to the booking airline */}
                        <div className="bg-white/5 border border-white/10 text-white rounded-md px-3 py-2 flex items-center justify-between">
                          <span>{flightAirline?.name || flightAirlineCode || 'Flight Airline'}</span>
                          <span className="text-xs text-white/40 bg-white/10 px-2 py-0.5 rounded">Matched to flight</span>
                        </div>
                        <p className="text-white/60 text-xs mt-1">
                          Credits can only be applied to the airline you're booking with
                        </p>
                      </div>

                      <div>
                        <Label className="text-white/60 text-sm mb-2 block">Credit Type</Label>
                        <Select 
                          value={formData.creditType} 
                          onValueChange={(value) => handleInputChange('creditType', value)}
                        >
                          <SelectTrigger className={`bg-white/5 border-white/10 text-white ${errors.creditType ? 'border-red-500' : ''}`}>
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-white/10">
                            {CREDIT_TYPES.map((type) => (
                              <SelectItem 
                                key={type.value} 
                                value={type.value}
                                className="text-white hover:bg-white/10"
                              >
                                <div className="flex items-center gap-2">
                                  <type.icon className="w-4 h-4" />
                                  {type.label}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {errors.creditType && (
                          <p className="text-red-400 text-xs mt-1">{errors.creditType}</p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-white/60 text-sm mb-2 block">
                          {formData.creditType === 'gift_card' ? 'Gift Card Number' : 'Credit / Confirmation Number'}
                        </Label>
                        <Input
                          value={formData.reference}
                          onChange={(e) => handleInputChange('reference', e.target.value.toUpperCase())}
                          placeholder="Enter number"
                          className={`bg-white/5 border-white/10 text-white placeholder:text-white/30 ${errors.reference ? 'border-red-500' : ''}`}
                        />
                        {errors.reference && (
                          <p className="text-red-400 text-xs mt-1">{errors.reference}</p>
                        )}
                      </div>

                      <div>
                        <Label className="text-white/60 text-sm mb-2 block">
                          PIN (if applicable)
                        </Label>
                        <Input
                          value={formData.pin}
                          onChange={(e) => handleInputChange('pin', e.target.value)}
                          placeholder="Optional"
                          type="password"
                          className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                        />
                      </div>
                    </div>

                    {formData.creditType === 'travel_credit' && (
                      <div>
                        <Label className="text-white/60 text-sm mb-2 block">
                          Last Name on Credit
                        </Label>
                        <Input
                          value={formData.claimantLastName}
                          onChange={(e) => handleInputChange('claimantLastName', e.target.value)}
                          placeholder="Last name as it appears on credit"
                          className={`bg-white/5 border-white/10 text-white placeholder:text-white/30 ${errors.claimantLastName ? 'border-red-500' : ''}`}
                        />
                        {errors.claimantLastName && (
                          <p className="text-red-400 text-xs mt-1">{errors.claimantLastName}</p>
                        )}
                        <p className="text-white/60 text-xs mt-1">
                          Must match the passenger who originally received the credit
                        </p>
                      </div>
                    )}

                    <div>
                      <Label className="text-white/60 text-sm mb-2 block">
                        Amount to Apply
                      </Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/60">$</span>
                        <Input
                          value={formData.claimedAmount}
                          onChange={(e) => {
                            const val = e.target.value.replace(/[^0-9.]/g, '');
                            if (val.split('.').length <= 2) {
                              handleInputChange('claimedAmount', val);
                            }
                          }}
                          placeholder="0.00"
                          className={`bg-white/5 border-white/10 text-white placeholder:text-white/30 pl-7 ${errors.claimedAmount ? 'border-red-500' : ''}`}
                        />
                      </div>
                      {errors.claimedAmount && (
                        <p className="text-red-400 text-xs mt-1">{errors.claimedAmount}</p>
                      )}
                      <p className="text-white/60 text-xs mt-1">
                        Maximum: ${fmtAmount(remainingBalance)} (remaining trip balance)
                      </p>
                    </div>

                    <p className="text-white/40 text-[10px] leading-relaxed">
                      By clicking "Apply Credit," you authorize Defera to apply this amount toward your booking. 
                      Invalid or insufficient credits will result in trip cancellation and forfeiture of any upfront payment. 
                      See our{' '}
                      <a href="/terms" className="text-[var(--accent-color)] hover:underline" target="_blank" rel="noopener noreferrer">Terms of Service</a>
                      {' '}for full details.
                    </p>

                    <div className="flex gap-3 pt-2">
                      <Button
                        onClick={() => {
                          setShowForm(false);
                          setErrors({});
                        }}
                        variant="ghost"
                        className="flex-1 text-white/60 hover:text-white hover:bg-white/10 font-normal"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleAddCredit}
                        className="flex-1 bg-[var(--accent-color)] hover:bg-[var(--accent-color)]/90 font-normal"
                      >
                        Apply Credit
                      </Button>
                    </div>
                  </div>
                </motion.div>
              )}

              {remainingBalance <= 0 && appliedCredits.length > 0 && (
                <p className="text-green-400/80 text-sm text-center mt-4 font-normal">
                  Credits fully cover your trip total
                </p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
